import { ApostleBountyFilterInput } from '@apeiron/apostle'
import { BountySortBy } from '@apeiron/bounty'
import { useFrontendConfigContext } from '@apeiron/config'
import {
  CurrencyTokenType,
  CurrencyTokenTypeERC20,
  useCurrencyContext
} from '@apeiron/currency'
import {
  ApeironPagingDataGridRef,
  createStrictContext,
  MayNull
} from '@apeiron/temp'
import { CreateBountyFormInputs } from '@src/components/apostles/bounty/CreateApostleBountyDialog'
import { NftType } from '@src/constants/bookmark'
import useApostleBountyFilterQuery from '@src/hooks/apeiron/apostle/useApostleBountyFilterQuery'
import useQueryPaging from '@src/hooks/common/router/useQueryPaging'
import useGetBounties, {
  FetchBountiesResponse
} from '@src/hooks/graphql/useGetBounties'
import { BountyFilterInput, BountyNode } from '@src/types/bounty'
import * as R from 'ramda'
import {
  Dispatch,
  ReactNode,
  RefObject,
  SetStateAction,
  useCallback,
  useRef,
  useState
} from 'react'

const [ContextProvider, useBountyContext] =
  createStrictContext<ContextType>('Bounty')

export { useBountyContext }

export const BountyProvider = (props: Props) => {
  const { changeUrlOnQuery = true, fixedFilter, children, nftType } = props

  const { config } = useFrontendConfigContext()

  const { getApeironPrice } = useCurrencyContext()

  const { fetch } = useGetBounties()

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  const [claimingBounty, setClaimingBounty] = useState<BountyNode | null>(null)

  const [duplicateModeOn, setDuplicateModeOn] = useState(false)

  const { queryPageNumber, setPageNumberQuery } = useQueryPaging()

  const gridRef = useRef<ApeironPagingDataGridRef>(null)

  const {
    filterInput: apostleBountyFilterInput,
    setFilterInput: setApostleBountyFilter
  } = useApostleBountyFilterQuery({
    supportUrl: changeUrlOnQuery
  })

  const defaultCurrency =
    config?.bounty.apostle.supportedCurrencies[0] ?? CurrencyTokenType.Aprs

  const defaultMinPrice = R.find(
    R.propEq(defaultCurrency, 'currencyCode'),
    config?.bounty.apostle.postSetting || []
  )

  const [defaultCreateFormValues, setDefaultCreateFormValues] =
    useState<CreateBountyFormInputs>({
      classId: null,
      minIV: 0,
      mainSkillId: null,
      otherSkillId1: null,
      otherSkillId2: null,
      mainSkillMinLevel: null,
      otherSkillMinLevel1: null,
      otherSkillMinLevel2: null,
      quantity: 1,
      price: getApeironPrice(defaultCurrency, defaultMinPrice?.minPrice),
      currency: defaultCurrency
    })

  const fetchBounties = useCallback(
    async (page: number, pageSize: number) => {
      const queryFilter = {
        nftType,
        apostleBountyFilter: {
          apostleTypes: apostleBountyFilterInput.classes,
          mainSkillFullIDs: apostleBountyFilterInput.firstSkillIDs,
          otherSkillsFullIDs: apostleBountyFilterInput.otherSkillIDs,
          minSkillLevelLowerBound:
            apostleBountyFilterInput.minSkillLevelLowerBound,
          minSkillLevelUpperBound:
            apostleBountyFilterInput.minSkillLevelUpperBound,
          minIVLowerBound: apostleBountyFilterInput.minIVLowerBound,
          minIVUpperBound: apostleBountyFilterInput.minIVUpperBound
        }
      }

      const res = await fetch({
        filterInput: fixedFilter || queryFilter,
        sortInput: {
          sortBy: BountySortBy.PriceDesc
        },
        pageInput: {
          page,
          pageSize
        }
      })

      return res
    },
    [
      nftType,
      apostleBountyFilterInput.classes,
      apostleBountyFilterInput.firstSkillIDs,
      apostleBountyFilterInput.otherSkillIDs,
      apostleBountyFilterInput.minSkillLevelLowerBound,
      apostleBountyFilterInput.minSkillLevelUpperBound,
      apostleBountyFilterInput.minIVLowerBound,
      apostleBountyFilterInput.minIVUpperBound,
      fixedFilter,
      fetch
    ]
  )

  const refetchBounties = useCallback(() => {
    gridRef.current?.refreshData()
  }, [])

  return (
    <ContextProvider
      value={{
        apostleBountyFilterInput,
        claimingBounty,
        defaultCreateFormValues,
        duplicateModeOn,
        isCreateDialogOpen,
        queryPageNumber,
        fetchBounties,
        refetchBounties,
        setApostleBountyFilter,
        setClaimingBounty,
        setDefaultCreateFormValues,
        setDuplicateModeOn,
        setIsCreateDialogOpen,
        setPageNumberQuery,
        supportedCurrencies: config?.bounty.apostle.supportedCurrencies || [],
        gridRef
      }}
    >
      {children}
    </ContextProvider>
  )
}

type ContextType = {
  apostleBountyFilterInput: ApostleBountyFilterInput
  claimingBounty: BountyNode | null
  defaultCreateFormValues: CreateBountyFormInputs
  duplicateModeOn: boolean
  isCreateDialogOpen: boolean
  queryPageNumber: number
  supportedCurrencies: CurrencyTokenTypeERC20[]
  fetchBounties: (
    page: number,
    pageSize: number
  ) => Promise<MayNull<FetchBountiesResponse>>
  refetchBounties: () => void
  setApostleBountyFilter: (filterInput: ApostleBountyFilterInput) => void
  setClaimingBounty: Dispatch<SetStateAction<BountyNode | null>>
  setDefaultCreateFormValues: Dispatch<SetStateAction<CreateBountyFormInputs>>
  setDuplicateModeOn: Dispatch<SetStateAction<boolean>>
  setIsCreateDialogOpen: Dispatch<SetStateAction<boolean>>
  setPageNumberQuery: (page: number) => void
  gridRef: RefObject<ApeironPagingDataGridRef>
}

type Props = {
  changeUrlOnQuery?: boolean
  children?: ReactNode
  fixedFilter?: MayNull<BountyFilterInput>
  nftType: NftType
}
